/* Modal de templates de documentos médicos — 4 templates editáveis */
import React, { useState, useCallback, useEffect, forwardRef } from 'react';
import type { DocumentTemplate } from '../types';

/** Chave de persistência no localStorage */
const STORAGE_KEY = 'drpera-ia-templates';

/** Templates padrão — PT-BR hardcoded como especificado no CLAUDE.md */
const TEMPLATES_PADRAO: DocumentTemplate[] = [
  {
    id: 'receita',
    label: 'Receita',
    content: `RECEITA MÉDICA

Paciente: _________________________________

[MEDICAMENTO] [apresentação] x [quantidade]
Posologia: [posologia]

Uso: [oral / tópico / IV / IM]

_________________________________
Dr(a). _________________________________
CRM-____ __________
Data: __/__/____`,
  },
  {
    id: 'atestado',
    label: 'Atestado',
    content: `ATESTADO MÉDICO

Atesto, para os devidos fins, que o(a) paciente
_________________________________
compareceu a atendimento médico nesta data, apresentando
_________________________________,
necessitando de afastamento de suas atividades por ___ dia(s),
a contar de ____/____/_______.

Código CID-10: ___________

_________________________________
Dr(a). _________________________________
CRM-____ __________
Data: __/__/____

(Conforme Código de Ética Médica — CEM/CFM)`,
  },
  {
    id: 'laudo-inss',
    label: 'Laudo INSS',
    content: `LAUDO MÉDICO PARA FINS PREVIDENCIÁRIOS

Paciente: _________________________________
Data de Nascimento: ____/____/________
CPF: _________________________________

DIAGNÓSTICO:
_________________________________
CID-10: ___________

ANAMNESE E EXAME FÍSICO:
_________________________________
_________________________________

EXAMES COMPLEMENTARES:
_________________________________

TRATAMENTO EM CURSO:
_________________________________

PROGNÓSTICO E CAPACIDADE LABORATIVA:
_________________________________

Estimativa de incapacidade: ___ dias / permanente / indefinida

_________________________________
Dr(a). _________________________________
CRM-____ __________ — Especialidade: _________________
Data: __/__/____`,
  },
  {
    id: 'soap',
    label: 'Evolução SOAP',
    content: `EVOLUÇÃO MÉDICA

Data: ____/____/_______ · Hora: ____:____

S — SUBJETIVO
Queixa principal: _________________________________
HDA: _________________________________

O — OBJETIVO
PA: ___ × ___ mmHg · FC: ___ bpm · FR: ___ irpm · Tax: ___°C
SpO₂: ___ % (ar ambiente / O₂ ___L/min)
Exame físico: _________________________________

A — AVALIAÇÃO
a) _________________________________
b) _________________________________
c) _________________________________
d) Outras considerações: _________________________________

P — PLANO
_________________________________
_________________________________

_________________________________
Dr(a). _________________________________ — CRM-____ __________`,
  },
];

/** Carrega templates do localStorage (ou usa os padrão) */
function carregarTemplates(): DocumentTemplate[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return TEMPLATES_PADRAO;
    const salvos = JSON.parse(raw) as Partial<DocumentTemplate>[];
    // Merge: mantém ids existentes, atualiza content se usuário editou
    return TEMPLATES_PADRAO.map((tpl) => {
      const salvo = salvos.find((s) => s.id === tpl.id);
      return salvo?.content ? { ...tpl, content: salvo.content } : tpl;
    });
  } catch {
    return TEMPLATES_PADRAO;
  }
}

/** Persiste apenas os que foram modificados */
function salvarTemplates(templates: DocumentTemplate[]): void {
  const modificados = templates
    .filter((tpl, i) => tpl.content !== TEMPLATES_PADRAO[i]?.content)
    .map(({ id, content }) => ({ id, content }));
  localStorage.setItem(STORAGE_KEY, JSON.stringify(modificados));
}

/**
 * Apaga os templates locais (logout completo, 21/08/2026 — PC compartilhada:
 * "sair" não deixa rastro). Ao logar de novo, voltam os defaults do idioma.
 */
export function clearStoredTemplates(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* localStorage indisponível — nada a apagar */
  }
}

/** Copia texto via clipboard (com fallback execCommand — padrão do projeto) */
function copiarTexto(text: string): boolean {
  try {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback execCommand (regra do projeto — useLaudo.ts)
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

interface TemplatesModalProps {
  onClose: () => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

/**
 * Modal de templates de documentos médicos.
 * 4 tabs: Receita, Atestado, Laudo INSS, Evolução SOAP.
 * Cada template: textarea editável + botão Copiar + botão Restaurar Padrão.
 * Persistência via localStorage.
 */
export const TemplatesModal = forwardRef<HTMLDivElement, TemplatesModalProps>(
  ({ onClose, t }, ref) => {
    const [templates, setTemplates] = useState<DocumentTemplate[]>(carregarTemplates);
    const [abaAtiva, setAbaAtiva] = useState(0);
    const [copiado, setCopiado] = useState(false);

    // Fecha com Escape
    useEffect(() => {
      function handleKey(e: KeyboardEvent) {
        if (e.key === 'Escape') onClose();
      }
      document.addEventListener('keydown', handleKey);
      return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const handleEditar = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setTemplates((prev) =>
          prev.map((tpl, i) => (i === abaAtiva ? { ...tpl, content: e.target.value } : tpl)),
        );
      },
      [abaAtiva],
    );

    const handleCopiar = useCallback(() => {
      const ok = copiarTexto(templates[abaAtiva]?.content ?? '');
      if (ok) {
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
      }
    }, [templates, abaAtiva]);

    const handleRestaurar = useCallback(() => {
      setTemplates((prev) =>
        prev.map((tpl, i) =>
          i === abaAtiva ? { ...tpl, content: TEMPLATES_PADRAO[i]?.content ?? '' } : tpl,
        ),
      );
    }, [abaAtiva]);

    const handleSalvar = useCallback(() => {
      salvarTemplates(templates);
      onClose();
    }, [templates, onClose]);

    const templateAtual = templates[abaAtiva];

    const tabLabels = [
      t('templates.tabs.recipe'),
      t('templates.tabs.certificate'),
      t('templates.tabs.laudoInss'),
      t('templates.tabs.soap'),
    ];

    return (
      /* Backdrop */
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('templates.title')}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(34, 37, 50, 0.88)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 600,
          padding: '20px',
        }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {/* Card central */}
        <div
          ref={ref}
          style={{
            maxWidth: '640px',
            width: '100%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--ink-soft)',
            border: '1px solid var(--violet)',
            boxShadow: '0 0 24px rgba(124,124,248,0.18)',
          }}
        >
          {/* Header do modal */}
          <div style={{ padding: '24px 32px 0' }}>
            <div
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: '15px',
                fontWeight: 700,
                color: '#FFFFFF',
                textShadow: 'none',
                letterSpacing: '0.04em',
                marginBottom: '20px',
              }}
            >
              {t('templates.title')}
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--ink-mute)' }}>
              {tabLabels.map((label, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setAbaAtiva(i)}
                  aria-selected={abaAtiva === i}
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: '12px',
                    fontWeight: abaAtiva === i ? 600 : 400,
                    padding: '7px 12px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom:
                      abaAtiva === i
                        ? '2px solid var(--violet)'
                        : '2px solid transparent',
                    color: abaAtiva === i ? '#FFFFFF' : 'var(--ink-mute)',
                    cursor: 'pointer',
                    letterSpacing: '0.03em',
                    transition: 'color 150ms, border-color 150ms',
                    marginBottom: '-1px',
                    textShadow: 'none',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Textarea editável */}
          <div style={{ padding: '20px 32px', flex: 1, overflowY: 'auto' }}>
            <textarea
              value={templateAtual?.content ?? ''}
              onChange={handleEditar}
              rows={14}
              aria-label={`Template: ${tabLabels[abaAtiva]}`}
              style={{
                width: '100%',
                padding: '12px',
                background: 'var(--ink)',
                border: '1px solid var(--ink-mute)',
                color: 'var(--ink-light)',
                fontFamily: 'var(--font-sans)',
                fontSize: '13px',
                lineHeight: 1.7,
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(124,124,248,0.50)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--ink-mute)')}
            />
          </div>

          {/* Rodapé com botões */}
          <div
            style={{
              padding: '16px 32px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              borderTop: '1px solid var(--ink-soft)',
            }}
          >
            {/* Ações do template atual */}
            <div style={{ display: 'flex', gap: '8px' }}>
              {/* COPIAR — verde somente no estado copiado=true (active/live state) */}
              <button
                type="button"
                onClick={handleCopiar}
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '8px 14px',
                  background: copiado ? 'var(--green-fluor)' : 'transparent',
                  border: `1px solid ${copiado ? 'var(--green-fluor)' : 'rgba(124,124,248,0.40)'}`,
                  color: copiado ? 'var(--ink)' : 'var(--ink-light)',
                  cursor: 'pointer',
                  letterSpacing: '0.02em',
                  boxShadow: copiado
                    ? '0 0 12px rgba(0,255,65,0.7), 0 0 28px rgba(0,255,65,0.35)'
                    : 'none',
                  transition: 'background 180ms ease, border-color 180ms ease, box-shadow 180ms ease, color 180ms ease',
                }}
              >
                {copiado ? `✓ ${t('templates.copy')}` : t('templates.copy')}
              </button>
              <button
                type="button"
                onClick={handleRestaurar}
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: '12px',
                  fontWeight: 400,
                  padding: '8px 14px',
                  background: 'transparent',
                  border: '1px solid var(--ink-soft)',
                  color: 'var(--ink-mute)',
                  cursor: 'pointer',
                  letterSpacing: '0.02em',
                  transition: 'border-color 180ms ease, color 180ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(124,124,248,0.40)';
                  e.currentTarget.style.color = 'var(--ink-light)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--ink-soft)';
                  e.currentTarget.style.color = 'var(--ink-mute)';
                }}
              >
                {t('templates.restore')}
              </button>
            </div>

            {/* Salvar e fechar */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '8px 14px',
                  background: 'transparent',
                  border: '1px solid rgba(124,124,248,0.40)',
                  color: 'var(--ink-light)',
                  cursor: 'pointer',
                  letterSpacing: '0.02em',
                  transition: 'box-shadow 180ms ease, border-color 180ms ease, color 180ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#FF6A00';
                  e.currentTarget.style.color = '#fff';
                  e.currentTarget.style.boxShadow = '0 0 14px rgba(255,106,0,0.8), 0 0 32px rgba(255,106,0,0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(124,124,248,0.40)';
                  e.currentTarget.style.color = 'var(--ink-light)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t('profile.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSalvar}
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '8px 14px',
                  background: 'var(--cta-blue)',
                  border: '1px solid var(--cta-blue)',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  letterSpacing: '0.02em',
                  boxShadow: 'none',
                  transition: 'box-shadow 180ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 14px rgba(0,96,240,0.7), 0 0 28px rgba(0,96,240,0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t('profile.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

TemplatesModal.displayName = 'TemplatesModal';
